import { downloadFile } from "@/core/utils";
import { isAPIError } from "@/core/services";
import { findExportFormatDetail } from "./export.types";
import type { ExportedFile, ExportFormat } from "./export.types";

/** Rótulo do formato nos toasts de sucesso e de erro: o Figma (frames "Exportar Tabela - Excel") chama o xlsx de "Excel". */
const EXPORT_TOAST_FORMAT_LABEL: Record<ExportFormat, string> = {
  pdf: "PDF",
  csv: "CSV",
  xlsx: "Excel",
};

/** Toast de sucesso (Figma 7238:34553, p3 de 21/10): "Tabela exportada em PDF com sucesso." */
export const buildExportSuccessMessage = (format: ExportFormat) =>
  `Tabela exportada em ${EXPORT_TOAST_FORMAT_LABEL[format]} com sucesso.`;
export const EXPORT_FORBIDDEN_SUMMARY = "Acesso não autorizado";
export const EXPORT_FORBIDDEN_MESSAGE = "Você não tem permissão para exportar esta listagem.";
export const EXPORT_EMPTY_SUMMARY = "Nada a exportar";
export const EXPORT_EMPTY_MESSAGE = "Não há registros na visualização atual.";

const FILE_NAME_DATE_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function toFileNameDate(date: Date): string {
  return FILE_NAME_DATE_FORMATTER.format(date).split("/").reverse().join("-");
}

export function buildExportFileName(
  fileName: string,
  format: ExportFormat,
  date = new Date(),
): string {
  return `${fileName}-${toFileNameDate(date)}.${findExportFormatDetail(format).extension}`;
}

/**
 * Nome do arquivo em `Content-Disposition`. `filename*` (RFC 5987) tem
 * precedência por preservar os acentos.
 */
export function parseContentDispositionFileName(header: string | null | undefined): string | null {
  if (!header) return null;

  const encoded = /filename\*\s*=\s*(?:[\w-]+)?'[^']*'([^;]+)/i.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim().replace(/^"|"$/g, "")) || null;
    } catch {
      // Codificação inválida: segue para o `filename` simples.
    }
  }

  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
  const name = (plain?.[2] ?? plain?.[1])?.trim();

  return name || null;
}

/**
 * Entrega o arquivo gerado pelo serviço. Sem nome ou tipo informados na
 * resposta, usa o padrão das listagens do Hub para o formato escolhido.
 */
export function downloadExportedFile(
  file: ExportedFile,
  fallbackName: string,
  format: ExportFormat,
): string {
  const detail = findExportFormatDetail(format);
  const fileName = file.fileName ?? buildExportFileName(fallbackName, format);

  downloadFile(file.content, fileName, file.mimeType ?? detail.mimeType);

  return fileName;
}

/** Toast de erro (Figma 7238:34553, p3 de 21/10): "Não foi possível exportar a tabela em PDF. Tente novamente." */
export const buildExportErrorMessage = (format: ExportFormat) =>
  `Não foi possível exportar a tabela em ${EXPORT_TOAST_FORMAT_LABEL[format]}. Tente novamente.`;

/** RN005 — a recusa do serviço por falta de autorização tem mensagem própria (sem frame no Figma); o resto segue o Figma. */
export function resolveExportError(
  error: unknown,
  format: ExportFormat,
): { summary: string; detail?: string } {
  if (isAPIError(error) && error.status === 403) {
    return { summary: EXPORT_FORBIDDEN_SUMMARY, detail: EXPORT_FORBIDDEN_MESSAGE };
  }

  return { summary: buildExportErrorMessage(format) };
}
